import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  arquivarAlimento,
  definirStatus,
  editarAlimento,
  getAlimento,
  getCriancaAtiva,
  getOfertasDoAlimento,
  registrarRecusa,
} from "../db/queries";
import { calcularDerivados, statusSugerido } from "../domain";
import {
  ESPECIALIDADE_LABEL,
  GRUPOS_ORDEM,
  GRUPO_LABEL,
  ORIGEM_LABEL,
  PASSO_ESCADA_LABEL,
  type GrupoAlimentar,
  type Oferta,
  type StatusAlimento,
} from "../types";
import { StatusBadge } from "../ui/StatusBadge";
import { EscadaTimeline } from "../ui/EscadaTimeline";
import { TendenciaLabel } from "../ui/Tendencia";
import { FoodPhoto } from "../ui/FoodPhoto";
import { fraseEvolucao } from "../lib/evolucao";
import { salvarFoto } from "../lib/foto";
import { SeletorCatalogo } from "../ui/SeletorCatalogo";
import { dataHora, diasAtras } from "../lib/format";

const STATUS_LABEL: Record<StatusAlimento, string> = {
  seguro: "seguro",
  em_teste: "em teste",
  nao_oferecido: "a experimentar",
  recusado: "recusado",
};
const ORDEM_STATUS: StatusAlimento[] = ["seguro", "em_teste", "nao_oferecido", "recusado"];

export function DetalheAlimento() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const alimento = useLiveQuery(() => getAlimento(id), [id]);
  const ofertas = useLiveQuery(() => getOfertasDoAlimento(id), [id]) ?? [];
  const crianca = useLiveQuery(getCriancaAtiva);
  const [mostrarCatalogo, setMostrarCatalogo] = useState(false);

  if (!alimento) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <div className="vazio">alimento não encontrado.</div>
      </main>
    );
  }

  const d = calcularDerivados(id, ofertas);
  const sugerido = statusSugerido(alimento.status, d);
  const sensorial = Object.entries(alimento.sensorial).filter(([, v]) => v);

  async function trocarFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fotoId = await salvarFoto(file);
    await editarAlimento(id, { fotos: [fotoId] });
  }

  async function escolherDoCatalogo(ref: string) {
    await editarAlimento(id, { fotos: [ref] });
    setMostrarCatalogo(false);
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>

      <div className="row" style={{ marginBottom: "var(--s4)" }}>
        <label className="foto-editavel" title="trocar foto">
          <FoodPhoto fotoId={alimento.fotos[0]} nome={alimento.nome} categoria={alimento.categoria} />
          <span className="foto-editavel-cam" aria-hidden>📷</span>
          <input type="file" accept="image/*" capture="environment" hidden onChange={trocarFoto} />
        </label>
        <div className="crescer">
          <h1 className="tela-titulo" style={{ marginBottom: 4 }}>
            {alimento.nome}
          </h1>
          {alimento.marca_produto && (
            <div className="meta" style={{ color: "var(--text-muted)" }}>
              {alimento.marca_produto}
            </div>
          )}
        </div>
        <StatusBadge status={alimento.status} />
      </div>

      <button
        className="btn btn-fantasma btn-bloco"
        style={{ marginBottom: "var(--s4)" }}
        onClick={() => setMostrarCatalogo((v) => !v)}
      >
        🖼️ {mostrarCatalogo ? "fechar catálogo" : "escolher imagem do catálogo"}
      </button>
      {mostrarCatalogo && (
        <div style={{ marginBottom: "var(--s4)" }}>
          <SeletorCatalogo selecionada={alimento.fotos[0]} onPick={escolherDoCatalogo} />
        </div>
      )}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        onClick={() => nav(`/registrar/${id}`)}
        style={{ marginBottom: "var(--s2)" }}
      >
        ➕ registrar oferta
      </button>
      {/* registro rápido de recusa: 1 toque, sem abrir o fluxo completo */}
      <button
        className="btn btn-fantasma btn-bloco"
        disabled={!crianca}
        onClick={() => crianca && registrarRecusa(id, crianca.id)}
        style={{ marginBottom: "var(--s5)" }}
      >
        ✕ registrar recusa
      </button>

      {/* P0.4 — contador + evolução na escada ao longo do tempo */}
      <section className="card">
        <div className="row-entre">
          <div>
            <div style={{ fontSize: "1.6rem", fontWeight: 500 }}>{d.n_ofertas}</div>
            <div className="meta" style={{ color: "var(--text-muted)" }}>
              {d.n_ofertas === 1 ? "exposição" : "exposições"} ·{" "}
              {diasAtras(d.dias_desde_ultima)}
            </div>
          </div>
          <TendenciaLabel tendencia={d.tendencia} />
        </div>

        <div style={{ marginTop: "var(--s4)" }}>
          <EscadaTimeline ofertas={ofertas} />
        </div>

        <p className="evol-frase">{fraseEvolucao(alimento.nome, ofertas, d)}</p>

        {d.passo_maximo !== null && (
          <div className="meta" style={{ color: "var(--text-muted)" }}>
            maior passo alcançado: {PASSO_ESCADA_LABEL[d.passo_maximo]}
          </div>
        )}
      </section>

      {/* status — toque pra marcar (o cuidador decide); o app só sugere */}
      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">status</h2>
        <div className="card">
          <div className="status-opcoes">
            {ORDEM_STATUS.map((s) => (
              <button
                key={s}
                type="button"
                className={`status-opt${alimento.status === s ? " sel" : ""}`}
                aria-pressed={alimento.status === s}
                onClick={() => definirStatus(id, s)}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
          <p className="status-regra">
            <strong>seguro</strong> = já come bem · <strong>em teste</strong> =
            experimentando · <strong>a experimentar</strong> = ainda não ofereceu ·
            <strong> recusado</strong> = não aceitou. você marca aqui; o app só{" "}
            <em>sugere</em> "seguro" quando a criança chega em "come um pouco" na escada.
          </p>
          {sugerido !== alimento.status && (
            <button
              className="btn btn-fantasma"
              style={{ marginTop: "var(--s2)" }}
              onClick={() => definirStatus(id, sugerido)}
            >
              sugestão pelos dados: {STATUS_LABEL[sugerido]} — aplicar
            </button>
          )}

          <div className="campo" style={{ margin: "var(--s4) 0 0" }}>
            <label>grupo</label>
            <select
              value={alimento.grupo ?? ""}
              onChange={(e) =>
                editarAlimento(id, {
                  grupo: (e.target.value || null) as GrupoAlimentar | null,
                })
              }
            >
              <option value="">—</option>
              {GRUPOS_ORDEM.map((g) => (
                <option key={g} value={g}>
                  {GRUPO_LABEL[g]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* tags sensoriais */}
      {sensorial.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">sensorial</h2>
          <div className="chips">
            {sensorial.map(([k, v]) => (
              <span className="chip" key={k}>
                {v}
              </span>
            ))}
          </div>
        </section>
      )}

      {alimento.observacoes && (
        <section className="secao">
          <h2 className="secao-titulo">observações</h2>
          <div className="card">{alimento.observacoes}</div>
        </section>
      )}

      {/* histórico */}
      <section className="secao">
        <h2 className="secao-titulo">histórico</h2>
        {ofertas.length === 0 ? (
          <div className="card" style={{ color: "var(--text-muted)" }}>
            sem ofertas ainda.
          </div>
        ) : (
          ofertas.map((o) => (
            <div className="card" key={o.id}>
              <div className="row-entre">
                <span style={{ fontWeight: 500 }}>
                  {PASSO_ESCADA_LABEL[o.passo_escada]}
                </span>
                <span className="meta" style={{ color: "var(--text-muted)" }}>
                  {dataHora(o.data_hora)}
                </span>
              </div>
              <div className="meta" style={{ color: "var(--text-suave)", marginTop: 4 }}>
                {origemDescricao(o)}
                {!o.confirmado && " · pendente"}
              </div>
            </div>
          ))
        )}
      </section>

      <button
        className="btn btn-bloco"
        style={{ marginTop: "var(--s4)", color: "var(--text-muted)" }}
        onClick={async () => {
          await arquivarAlimento(id);
          nav("/biblioteca", { replace: true });
        }}
      >
        arquivar alimento
      </button>
    </main>
  );
}

/** Descrição humana da origem do registro, com contexto de terapia quando houver. */
function origemDescricao(o: Oferta): string {
  if (o.origem === "crianca") return "escolha da criança";
  if (o.origem === "profissional") {
    const esp = o.profissional_especialidade
      ? ESPECIALIDADE_LABEL[o.profissional_especialidade]
      : null;
    const detalhe = [esp, o.profissional_nome].filter(Boolean).join(" · ");
    return detalhe ? `${ORIGEM_LABEL.profissional} · ${detalhe}` : ORIGEM_LABEL.profissional;
  }
  return "registro do cuidador";
}
